<template>
  <portal to="dropdown">
    <div v-if="open" class="action-menu" ref="menu" :style="menuStyle" @click.stop>
      <div class="menu-item" @click="handleModify">
        <span class="menu-item-text">{{ $t('variableActions.modify') }}</span>
      </div>
      <div class="menu-item menu-item-danger" @click="handleDelete">
        <span class="menu-item-text">{{ $t('variableActions.delete') }}</span>
      </div>
    </div>
  </portal>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'

@Component
export default class VariableActionMenu extends Vue {
  @Prop({ required: true }) open!: boolean
  @Prop() onModify?: () => void
  @Prop() onDelete?: () => void
  @Prop() targetElement?: HTMLElement | null

  @Ref('menu') menuRef!: HTMLDivElement

  private handleGlobalClose = () => {
    this.$emit('close')
  }

  menuStyle: {
    position?: string
    top?: string
    left?: string
    right?: string
    bottom?: string
    zIndex?: string
    transform?: string
  } = {}

  @Watch('open')
  watchOpen(newVal: boolean) {
    if (newVal) {
      // Position menu relative to target element
      this.updatePosition()
      // Close menu when clicking outside
      this.$nextTick(() => {
        document.addEventListener('click', this.handleClickOutside)
      })
    } else {
      document.removeEventListener('click', this.handleClickOutside)
    }
  }

  @Watch('targetElement')
  watchTargetElement() {
    if (this.open) {
      this.updatePosition()
    }
  }

  updatePosition() {
    if (!this.targetElement) {
      // Fallback: position near mouse or center of screen
      this.menuStyle = {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: '10001'
      }
      return
    }

    this.$nextTick(() => {
      if (!this.targetElement) return

      const rect = this.targetElement.getBoundingClientRect()

      // Position menu below and aligned to the right of the icon
      // Use fixed positioning relative to viewport
      this.menuStyle = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.right - 120}px`, // 120px is approximate menu width
        zIndex: '10001'
      }
    })
  }

  handleClickOutside(event: MouseEvent) {
    // Don't close if clicking on the target element
    if (this.targetElement && this.targetElement.contains(event.target as Node)) {
      return
    }
    if (this.menuRef && !this.menuRef.contains(event.target as Node)) {
      this.$emit('close')
    }
  }

  handleModify() {
    if (this.onModify) {
      this.onModify()
    }
    this.$emit('modify')
    this.$emit('close')
  }

  handleDelete() {
    if (this.onDelete) {
      this.onDelete()
    }
    this.$emit('delete')
    this.$emit('close')
  }

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('variable-action-menu-close', this.handleGlobalClose)
  }

  mounted() {
    window.addEventListener('variable-action-menu-close', this.handleGlobalClose)
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles' as *;

.action-menu {
  @include notch-style-floating;
  position: fixed !important;
  background: $notch-color-neutral-grey-000;
  border-radius: $notch-radius-2x;
  padding: $notch-space-1x;
  min-width: $notch-space-30x;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10001 !important;
  pointer-events: auto;
  visibility: visible !important;
}

.menu-item {
  @include notch-text-label-light-M;
  color: $notch-color-neutral-grey-900;
  padding: $notch-space-2x $notch-space-3x;
  border-radius: $notch-radius-1x;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: $notch-color-neutral-sand-100;
  }

  &.menu-item-danger {
    color: $notch-color-semantic-alert-800;

    &:hover {
      background: $notch-color-semantic-alert-100;
    }
  }

  .menu-item-text {
    display: block;
  }
}
</style>
